/**
 * Nonce store - wallet address → nonce state in local browser storage
 *
 * Persists per-wallet nonce state (confirmed, pending, lastSync) to localStorage
 * so it survives refresh and can hydrate NonceManager. Integrated with
 * sdk/utils/nonceManager via an optional storage adapter.
 */

import { create } from 'zustand';

const STORAGE_KEY = 'mormlayer-nonce-store';

/** Persisted nonce state for one wallet (no mutex - runtime only in NonceManager). */
export interface PersistedNonceState {
  confirmed: number;
  pending: number[];
  lastSync: number;
}

/** Stored shape: normalized wallet address → persisted state. */
export type NonceStoreSnapshot = Record<string, PersistedNonceState>;

function normalizeOwner(owner: string): string {
  return owner.toLowerCase();
}

function loadFromStorage(): NonceStoreSnapshot {
  if (typeof window === 'undefined' || !window.localStorage) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as NonceStoreSnapshot;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: NonceStoreSnapshot = {};
    for (const [key, val] of Object.entries(parsed)) {
      if (
        val &&
        typeof val.confirmed === 'number' &&
        Array.isArray(val.pending) &&
        typeof val.lastSync === 'number'
      ) {
        out[key] = {
          confirmed: val.confirmed,
          pending: val.pending,
          lastSync: val.lastSync,
        };
      }
    }
    return out;
  } catch {
    return {};
  }
}

function saveToStorage(snapshot: NonceStoreSnapshot): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

export interface NonceStoreState {
  /** Normalized owner → persisted nonce state. */
  byOwner: NonceStoreSnapshot;

  /** Get persisted state for a wallet (for hydration). */
  getNonceState: (owner: string) => PersistedNonceState | undefined;

  /** Set persisted state for a wallet (called by NonceManager after changes). */
  setNonceState: (owner: string, state: PersistedNonceState) => void;

  /** Remove state for one wallet. */
  clearOwner: (owner: string) => void;

  /** Remove all persisted nonce state. */
  clearAll: () => void;

  /** Full snapshot for adapter (all owners). */
  getSnapshot: () => NonceStoreSnapshot;
}

export const useNonceStore = create<NonceStoreState>((set, get) => ({
  byOwner: loadFromStorage(),

  getNonceState: (owner: string) => {
    return get().byOwner[normalizeOwner(owner)];
  },

  setNonceState: (owner: string, state: PersistedNonceState) => {
    const key = normalizeOwner(owner);
    set((prev) => {
      const next = { ...prev.byOwner, [key]: state };
      saveToStorage(next);
      return { byOwner: next };
    });
  },

  clearOwner: (owner: string) => {
    const key = normalizeOwner(owner);
    set((prev) => {
      const { [key]: _, ...rest } = prev.byOwner;
      const next = rest as NonceStoreSnapshot;
      saveToStorage(next);
      return { byOwner: next };
    });
  },

  clearAll: () => {
    saveToStorage({});
    set({ byOwner: {} });
  },

  getSnapshot: () => get().byOwner,
}));
